import React from "react"
import cx from "classnames"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import { Kbd, KbdGroup } from "../kbd"
import { KitLogoMark } from "../kit-logo"
import { Tooltip } from "../tooltip"
import { VisuallyHidden } from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationTriggerProps = {
  /**
   * Accessible label for the button. Defaults to "Collapse sidebar" when
   * expanded and "Expand sidebar" when collapsed.
   */
  label?: string
  /**
   * Icon to show. Defaults to `sidebar-left` when expanded and `sidebar-right`
   * when collapsed.
   */
  icon?: IconName
  /**
   * Keys rendered as a shortcut hint beside the tooltip label (e.g.
   * `["⌘", "\\"]`). Presentational only — this component binds no keyboard
   * handler, so the consumer owns both the binding and the platform-correct
   * modifier. Kept out of the accessible name.
   */
  keys?: string[]
}

// =============================================================================
// Styles
// =============================================================================

const triggerStyles = [
  "flex items-center justify-center",
  "rounded-[10px] p-2",
  "text-gray-900 hover:bg-gray-900/[0.06]",
  "outline-2 outline-transparent -outline-offset-2 focus-visible:outline-gray-900",
  "transition-colors"
].join(" ")

const logoTransitionStyles =
  "transition-[opacity,scale,filter] duration-100 ease-[ease-out] motion-reduce:transition-none"

// =============================================================================
// Component
// =============================================================================

/**
 * A button that toggles the sidebar between expanded and collapsed.
 * Reads collapse state from the nearest `Navigation` context.
 * Place it anywhere inside a `Navigation` — typically inside `Navigation.Header`.
 *
 * When the desktop sidebar is collapsed the button shows the Kit logo mark (the
 * "K"), which crossfades to the expand icon while the sidebar is hovered.
 */
export function NavigationTrigger({
  label,
  icon,
  keys = []
}: NavigationTriggerProps) {
  const { collapsed, setCollapsed, mobile } = useNavigationContext()

  const defaultLabel = collapsed ? "Expand sidebar" : "Collapse sidebar"
  const defaultIcon: IconName = collapsed ? "sidebar-right" : "sidebar-left"

  const showLogoSwap = collapsed && !mobile
  const tooltipLabel = label ?? defaultLabel

  const trigger = (
    <button
      type="button"
      data-slot="navigation-trigger"
      aria-expanded={!collapsed}
      className={cx(triggerStyles, mobile && "hidden")}
      onClick={() => setCollapsed(!collapsed)}
    >
      <VisuallyHidden>{tooltipLabel}</VisuallyHidden>
      {showLogoSwap ? (
        <span
          data-slot="navigation-collapsed-logo"
          className="pointer-events-none grid place-items-center"
          aria-hidden
        >
          <KitLogoMark
            className={cx(
              "col-start-1 row-start-1 h-6 w-auto",
              logoTransitionStyles,
              "group-hover/sidebar:scale-[0.7] group-hover/sidebar:opacity-0 group-hover/sidebar:blur-[2px]",
              "group-has-[:focus-visible]/sidebar:scale-[0.7] group-has-[:focus-visible]/sidebar:opacity-0 group-has-[:focus-visible]/sidebar:blur-[2px]"
            )}
          />
          <Icon
            name={icon ?? defaultIcon}
            className={cx(
              "col-start-1 row-start-1 scale-[0.7] opacity-0 blur-[2px]",
              logoTransitionStyles,
              "group-hover/sidebar:scale-100 group-hover/sidebar:opacity-100 group-hover/sidebar:blur-[0px]",
              "group-has-[:focus-visible]/sidebar:scale-100 group-has-[:focus-visible]/sidebar:opacity-100 group-has-[:focus-visible]/sidebar:blur-[0px]"
            )}
          />
        </span>
      ) : (
        <Icon name={icon ?? defaultIcon} />
      )}
    </button>
  )

  // The trigger lives in the header, outside NavigationMenu's Tooltip.Provider,
  // so it brings its own. Disabled on mobile, where the trigger is hidden.
  return (
    <Tooltip.Provider delay={0}>
      <Tooltip disabled={mobile}>
        <Tooltip.Trigger render={trigger} />
        <Tooltip.Content position="right" sideOffset={12}>
          {tooltipLabel}
          {keys.length > 0 && (
            <KbdGroup className="-mr-2" aria-hidden>
              {keys.map(key => (
                <Kbd key={key} variant="dark">
                  {key}
                </Kbd>
              ))}
            </KbdGroup>
          )}
        </Tooltip.Content>
      </Tooltip>
    </Tooltip.Provider>
  )
}

NavigationTrigger.displayName = "Navigation.Trigger"
